/**
 * ตรวจรูปแบบตัวเลขที่ผู้ปฏิบัติงานพิมพ์บนเครื่องสแกน
 *
 * ตรวจ **รูปแบบของสตริง** ไม่ใช่ค่าที่แปลงเป็นตัวเลขแล้ว เพราะ `NUMERIC(18,4)`
 * ที่ผ่าน IEEE-754 double คือจำนวนที่เพี้ยนไปเงียบ ๆ และค่าที่พิมพ์ต้องเดินทางถึง
 * เซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร (`docs/API_CONTRACT.md` — qty ทุกตัวของ
 * scan-out/staging เป็น string ทศนิยม, JSON number ตอบ `400`)
 *
 * ⚠️ นี่คือ **การตรวจที่หน้าจอเพื่อให้ผู้ใช้รู้ผลทันที ไม่ใช่ความจริงชุดที่สอง**
 * เซิร์ฟเวอร์ยังเป็นผู้ตัดสินทุกข้อเสมอเมื่อคำสั่งถูก sync ขึ้นไปภายหลัง
 *
 * ⚠️ ไม่ได้ import ตัวตรวจชุดเดียวกันจาก `lib/bom/types.ts` ทั้งที่ pattern
 * เหมือนกัน เพราะโมดูลนั้นเป็นของหน้าจอ BOM ฝั่ง Desktop (พา type/ตัวช่วยของ
 * ฟอร์ม BOM ทั้งชุดเข้ามาใน bundle ของเครื่องสแกนที่ต้องเบาและทำงานออฟไลน์) —
 * เหตุผลเดียวกับที่ `lib/goodsReceipts/idempotency.ts` ไม่ยืมตัวสุ่มของคิวออฟไลน์
 * ถ้าวันหนึ่งทั้งสองฝั่งต้องใช้ตัวเดียวกันจริง ควรยกขึ้นเป็น helper กลาง
 */

/** ทศนิยมไม่ติดลบในรูปแบบที่ `NUMERIC` รับ — เครื่องหมายลบตกตั้งแต่ที่นี่ */
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

/** จำนวนเต็มบวกล้วน ใช้กับ `operation_seq` ซึ่ง contract ส่งเป็น JSON number */
const INTEGER_PATTERN = /^\d+$/;

export function isNonNegativeDecimal(value: string): boolean {
  return DECIMAL_PATTERN.test(value);
}

/**
 * มากกว่าศูนย์จริง — "มีเลขที่ไม่ใช่ 0 อย่างน้อยหนึ่งตัว" ตัดสินจากสตริงตรง ๆ
 * (`0.0000` เป็นเท็จ, `0.0001` เป็นจริง) โดยไม่แตะ `Number` เลย
 */
export function isPositiveDecimal(value: string): boolean {
  return DECIMAL_PATTERN.test(value) && /[1-9]/.test(value);
}

export function isPositiveInteger(value: string): boolean {
  return INTEGER_PATTERN.test(value) && /[1-9]/.test(value);
}

/**
 * ทั้งสามจำนวนของ Scan-Out เป็นศูนย์หมด — เงาของ `422 NOTHING_CONFIRMED`
 *
 * "การปิด cycle ที่ไม่ได้ผลิตอะไรไม่ใช่การปิดงาน" (`docs/API_CONTRACT.md`)
 * หน้าจอปฏิเสธก่อนเพื่อไม่ให้คำสั่งที่รู้อยู่แล้วว่าจะถูกปฏิเสธไปนอนรอในคิว
 * จนกว่าจะ sync ได้ แล้วค่อยเด้งกลับมาเป็น conflict ให้คนตามแก้ทีหลัง
 */
export function confirmsNothing(
  qtyGood: string,
  qtyScrap: string,
  qtyRework: string,
): boolean {
  return [qtyGood, qtyScrap, qtyRework].every(
    (value) => isNonNegativeDecimal(value) && !isPositiveDecimal(value),
  );
}
